import type { eventWithTime } from '@rrweb/types';
import { CLIENT_HEARTBEAT_INTERVAL_MS, COBROWSE_PROTOCOL_VERSION, CobrowseRole, KEEPALIVE_INTERVAL_MS, KEEPALIVE_TIMEOUT_MS } from '../protocol';
import type { AgentCommand, ClientMessage, JoinSessionResponse, NodeTarget, RelayMessage, SessionEndReason } from '../protocol';
import { CobrowseRecorder } from './CobrowseRecorder';
import type { CobrowseRelayClient } from './CobrowseRelayClient';
import { CobrowseSocket, buildConnectUrl } from './CobrowseSocket';
import { CommandExecutor } from './CommandExecutor';
import type { CursorKind, ScreenPoint } from './CommandExecutor';
import { FieldSelectionTracker } from './FieldSelectionTracker';
import { KeepAlive } from './KeepAlive';
import { PseudoClassEmulator } from './PseudoClassEmulator';
import { encodeRecording } from './RecordingCodec';
import { ScreenWakeLock } from './ScreenWakeLock';

export type CobrowseClientStatus = 'joining' | 'waiting-agent' | 'live' | 'reconnecting' | 'ended';

export interface RemoteCursor extends ScreenPoint {
    kind: CursorKind;
}

export interface SelectPreview {
    rect: { left: number; top: number; width: number; height: number };
    options: { value: string; label: string }[];
    currentValue: string;
    highlightedValue: string | null;
}

export interface CobrowseClientCallbacks {
    onStatus: (status: CobrowseClientStatus) => void;
    onCursor: (cursor: RemoteCursor | null) => void;
    onSelectPreview: (preview: SelectPreview | null) => void;
    onClick: (point: ScreenPoint) => void;
    onEnded: (reason: SessionEndReason | 'connection-lost') => void;
}

// About a minute of retries: long enough for a phone to come back from a lock or a network switch.
const RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000, 15_000, 30_000];

export class CobrowseClientSession {
    private socket: CobrowseSocket<AgentCommand | RelayMessage, ClientMessage> | null = null;
    private readonly recorder = new CobrowseRecorder((events) => this.sendRecording(events));
    private executor: CommandExecutor | null = null;
    private pointerTarget: NodeTarget | null = null;
    private pseudoClassEmulator: PseudoClassEmulator | null = null;
    private fieldSelectionTracker: FieldSelectionTracker | null = null;
    private selectPreview: { nodeId: number; highlightedValue: string | null } | null = null;
    private batchId = 0;
    private hasEnded = false;
    private clientToken: string | null = null;
    private reconnectAttempt = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private readonly keepAlive = new KeepAlive(
        () => this.socket?.ping(),
        () => this.dropSocket(),
        KEEPALIVE_INTERVAL_MS,
        KEEPALIVE_TIMEOUT_MS
    );

    private readonly wakeLock = new ScreenWakeLock();

    public readonly sessionId: string;
    private readonly relayUrl: string;
    private readonly relayClient: CobrowseRelayClient;
    private readonly callbacks: CobrowseClientCallbacks;

    constructor(sessionId: string, relayUrl: string, relayClient: CobrowseRelayClient, callbacks: CobrowseClientCallbacks) {
        this.sessionId = sessionId;
        this.relayUrl = relayUrl;
        this.relayClient = relayClient;
        this.callbacks = callbacks;
    }

    async join(authorization: string): Promise<JoinSessionResponse> {
        this.callbacks.onStatus('joining');

        const joined = await this.relayClient.join(this.sessionId, authorization);

        this.clientToken = joined.clientToken;
        this.connect();
        window.addEventListener('scroll', this.refreshOverlays, { capture: true, passive: true });
        document.addEventListener('visibilitychange', this.resumeOnReturn);
        window.addEventListener('online', this.resumeOnReturn);

        return joined;
    }

    private connect(): void {
        if (!this.clientToken || this.hasEnded) {
            return;
        }

        const socket: CobrowseSocket<AgentCommand | RelayMessage, ClientMessage> = new CobrowseSocket(buildConnectUrl(this.relayUrl, this.sessionId, this.clientToken), {
            onOpen: () => {
                this.reconnectAttempt = 0;
                this.keepAlive.start();
                this.startHeartbeat();
                this.callbacks.onStatus('waiting-agent');
            },
            onPong: () => this.keepAlive.pong(),
            onMessage: (message) => this.handle(message),
            onClose: () => {
                if (this.socket === socket) {
                    this.dropSocket();
                }
            },
        });
        this.socket = socket;
    }

    // A dead socket can take minutes to fire `close`: stop waiting for it and reconnect at once. The
    // agent re-requests the screen when it sees this client come back (`peer:joined`).
    private dropSocket(): void {
        const dead = this.socket;
        this.socket = null;
        this.keepAlive.stop();
        this.stopHeartbeat();
        dead?.close();

        if (this.hasEnded) {
            return;
        }

        this.hidePointer();
        this.closeSelectPreview();
        this.scheduleReconnect();
    }

    private scheduleReconnect(): void {
        const delay = RECONNECT_DELAYS_MS[this.reconnectAttempt];

        if (delay === undefined) {
            this.end('connection-lost');
            return;
        }

        this.reconnectAttempt++;
        this.callbacks.onStatus('reconnecting');
        this.reconnectTimer = setTimeout(() => this.connect(), delay);
    }

    private readonly resumeOnReturn = (): void => {
        if (document.visibilityState !== 'visible' || this.hasEnded) {
            return;
        }

        if (this.socket) {
            this.keepAlive.probe();
            return;
        }

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
            this.connect();
        }
    };

    private startHeartbeat(): void {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => this.socket?.send({ type: 'client:heartbeat' }), CLIENT_HEARTBEAT_INTERVAL_MS);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    leave(): void {
        this.socket?.send({ type: 'session:end' });
        this.end('ended-by-client');
    }

    private async handle(message: AgentCommand | RelayMessage): Promise<void> {
        switch (message.type) {
            case 'peer:joined':
                if (message.role === CobrowseRole.Agent) {
                    await this.startStreaming();
                }
                return;
            case 'peer:left':
                this.hidePointer();
                this.closeSelectPreview();
                this.callbacks.onStatus('waiting-agent');
                return;
            case 'session:ended':
                this.end(message.reason);
                return;
            case 'snapshot:request':
                this.recorder.takeFullSnapshot();
                return;
            case 'pointer:move':
                this.pointerTarget = message.target;
                this.pseudoClassEmulator?.hover(this.executor?.elementOf(message.target.nodeId) ?? null);
                this.refreshOverlays();
                return;
            case 'pointer:hide':
                this.hidePointer();
                return;
            case 'select:open':
                this.selectPreview = { nodeId: message.nodeId, highlightedValue: null };
                this.refreshOverlays();
                return;
            case 'select:highlight':
                if (this.selectPreview) {
                    this.selectPreview.highlightedValue = message.value;
                    this.refreshOverlays();
                }
                return;
            case 'select:close':
                this.closeSelectPreview();
                return;
            case 'session:end':
                return;
            default:
                this.execute(message);
        }
    }

    private async startStreaming(): Promise<void> {
        await this.recorder.start();
        this.executor ??= new CommandExecutor(this.recorder.nodes);
        this.pseudoClassEmulator ??= new PseudoClassEmulator();
        this.fieldSelectionTracker ??= new FieldSelectionTracker(this.recorder.nodes, (selection) => this.socket?.send({ type: 'field:selection', selection }));
        this.socket?.send({ type: 'client:hello', protocolVersion: COBROWSE_PROTOCOL_VERSION });
        this.wakeLock.acquire();
        this.recorder.takeFullSnapshot();
        this.callbacks.onStatus('live');
    }

    private execute(command: Parameters<CommandExecutor['execute']>[0]): void {
        if (!this.executor) {
            return;
        }

        if (command.type === 'click') {
            this.pointerTarget = command.target;
            const point = this.executor.resolvePoint(command.target);

            if (point) {
                this.callbacks.onClick(point);
            }
        }

        if (command.type === 'select:change') {
            this.closeSelectPreview();
        }

        const executor = this.executor;
        const rejection = this.recorder.withoutRecordingInteractions(() => executor.execute(command));
        this.pseudoClassEmulator?.syncFocus();

        if (rejection) {
            this.socket?.send({ type: 'command:rejected', command: command.type, reason: rejection });
        }
    }

    private sendRecording(events: eventWithTime[]): void {
        this.batchId = (this.batchId + 1) % 0xffffffff;
        encodeRecording(events, this.batchId).forEach((frame) => this.socket?.sendBinary(frame));
    }

    private readonly refreshOverlays = (): void => {
        if (!this.executor) {
            return;
        }

        const point = this.pointerTarget ? this.executor.resolvePoint(this.pointerTarget) : null;
        this.callbacks.onCursor(point && this.pointerTarget ? { ...point, kind: this.executor.resolveCursor(this.pointerTarget) } : null);
        this.callbacks.onSelectPreview(this.buildSelectPreview());
    };

    private buildSelectPreview(): SelectPreview | null {
        const select = this.selectPreview ? this.executor?.elementOf(this.selectPreview.nodeId) : null;

        if (!this.selectPreview || !(select instanceof HTMLSelectElement)) {
            return null;
        }

        const { left, top, width, height } = select.getBoundingClientRect();

        return {
            rect: { left, top, width, height },
            options: Array.from(select.options).map((option) => ({ value: option.value, label: option.text })),
            currentValue: select.value,
            highlightedValue: this.selectPreview.highlightedValue,
        };
    }

    private hidePointer(): void {
        this.pointerTarget = null;
        this.pseudoClassEmulator?.hover(null);
        this.callbacks.onCursor(null);
    }

    private closeSelectPreview(): void {
        this.selectPreview = null;
        this.callbacks.onSelectPreview(null);
    }

    private end(reason: SessionEndReason | 'connection-lost'): void {
        if (this.hasEnded) {
            return;
        }

        this.hasEnded = true;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }

        this.keepAlive.stop();
        this.stopHeartbeat();
        this.wakeLock.release();
        document.removeEventListener('visibilitychange', this.resumeOnReturn);
        window.removeEventListener('online', this.resumeOnReturn);
        this.recorder.stop();
        this.executor?.destroy();
        this.pseudoClassEmulator?.destroy();
        this.fieldSelectionTracker?.destroy();
        this.socket?.close();
        window.removeEventListener('scroll', this.refreshOverlays, { capture: true });
        this.callbacks.onCursor(null);
        this.callbacks.onSelectPreview(null);
        this.callbacks.onStatus('ended');
        this.callbacks.onEnded(reason);
    }
}
